// Build approval — one route (Oct 22, 2026).
//
// Greg: "I want 1 bottleneck for Chad's approval board. Allow Robert to see
// and approve builds submitted as well."
//
// Every submitted build (stage build_submitted) is approved on the Build
// Approvals board (Dispatch → Chad's view or Robert's view) and nowhere else.
// The board calls build_approve() / build_send_back(); those RPCs are the only
// way a signed-in person moves a build out of build_submitted, and the database
// refuses every other stage write into schedule_build or build_scheduled from
// before approval (deals_gate_build_approval, once
// finance_settings.build_approval_single_route is on).
//
// ⚠ BUILD_APPROVER_ROLES mirrors SQL build_can_approve(). Change both together.

import { supabase } from "./supabase";

/** Who may approve or send back a submitted build. Super users read as admin. */
export const BUILD_APPROVER_ROLES = new Set([
  "admin",
  "sales_director",
  "roof_team_manager",
  "roof_director",
  "director_of_operations",
]);

export const BUILD_APPROVERS = "Chad or Robert";
export const BUILD_APPROVAL_BOARD = "Build Approvals board";

export function canApproveBuilds(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || (!!role && BUILD_APPROVER_ROLES.has(role));
}

/** The board, on the view the person already works from. */
export function buildApprovalsLink(role: string | null | undefined): string {
  return role === "roof_team_manager" ? "/dispatch?view=robert&tab=approvals" : "/dispatch?view=chad&tab=approvals";
}

/** The database's reasons, carried on the error's hint. */
export const BUILD_GATE_HINTS = new Set([
  "build_submit_gate",
  "build_payment_plan_required",
  "build_approve_on_board",
  "build_submit_first",
  "build_skip_leadership",
  "build_back_to_schedule_leadership",
  "build_approver_only",
  "build_not_submitted",
  "build_send_back_reason",
]);

/** The gate hint on a refused write, or null when the error is something else. */
export function buildGateHint(e: unknown): string | null {
  const hint = (e as { hint?: unknown } | null)?.hint;
  return typeof hint === "string" && BUILD_GATE_HINTS.has(hint) ? hint : null;
}

export interface BuildApproveResult {
  ok: boolean;
  deal_id: string;
  stage: string;
  approved_by: string | null;
}

/** Approve a submitted build: build_submitted → schedule_build. */
export async function approveBuild(dealId: string, note?: string | null): Promise<BuildApproveResult> {
  const { data, error } = await supabase.rpc("build_approve", { p_deal_id: dealId, p_note: note ?? null });
  if (error) throw error;
  return data as BuildApproveResult;
}

/** Send a submitted build back to the RM: build_submitted → install_meeting. */
export async function sendBuildBack(dealId: string, reason: string): Promise<{ ok: boolean; deal_id: string; stage: string }> {
  const { data, error } = await supabase.rpc("build_send_back", { p_deal_id: dealId, p_reason: reason });
  if (error) throw error;
  return data as { ok: boolean; deal_id: string; stage: string };
}

// ---------------------------------------------------------------------------
// Where a deal stands with its build, read from the STAGE (Oct 23, 2026).
//
// The Scope card used to key its "Submit build" button and banners on
// agreement_status alone, and only its own button ever moved that column past
// 'ready_for_dispatch'. The deal-page submit, the pipeline dropdown and
// build_approve() all move the stage without touching it, so a deal Chad had
// approved on the board still read "Ready to submit build". The stage is the
// truth: build_submitted = on the board, schedule_build and later = approved.
// ---------------------------------------------------------------------------

export type BuildSubmitState = "before" | "submitted" | "approved";

/** Stages a build only reaches once it has been approved on the board. */
export const BUILD_APPROVED_STAGES = new Set([
  "schedule_build",
  "build_scheduled",
  "roof_complete",
  "ready_for_supps",
  "ready_for_updated_invoice",
  "supps_waiting_on_claims",
  "supps_waiting_on_final_invoice",
  "collecting",
  "delinquent",
  "register_oc_warranty",
  "closed_won",
]);

/** before = not submitted yet · submitted = waiting on the board · approved = Chad or Robert approved it. */
export function buildSubmitState(stage: string | null | undefined): BuildSubmitState {
  if (stage === "build_submitted") return "submitted";
  if (stage && BUILD_APPROVED_STAGES.has(stage)) return "approved";
  return "before";
}
